<template>
  <div class="task-detail-accounts" data-testid="setup-task-detail__accounts-panel">
    <lb-datatableaction
      :headers="headers"
      :tabledata="accountRows"
      :displayed-row-count="accountRows.length"
      :itemsperpage="accountRows.length || 25"
      :bulk-actions="false"
      data-testid="setup-task-detail__accounts-table"
      header-bg-class="fbc-table-header-bg"
    >
      <template #tbody>
        <tr
          v-for="(item, index) in accountRows"
          :key="item._id || item.glcode || index"
          class="task-detail-accounts__row"
          data-testid="setup-task-detail__accounts-row"
        >
          <td
            v-for="header in visibleHeaders"
            :key="`${header.value}-${index}`"
            class="px-4 single-line"
            :data-testid="`setup-task-detail__accounts-cell--${header.value}-${index}`"
          >
            <div
              v-if="header.value === 'location'"
              class="task-detail-accounts__location-cell"
              data-testid="setup-task-detail__accounts-location-cell"
            >
              <OverflowList
                :items="locationItems(item)"
                data-testid="setup-task-detail__accounts-location-overflow"
              />
            </div>
            <span v-else>{{ item[header.value] || '—' }}</span>
          </td>
        </tr>

        <tr v-if="!accountRows.length">
          <td
            :colspan="visibleHeaders.length"
            class="px-4 py-6 text-center gray--text"
            data-testid="setup-task-detail__accounts-empty"
          >
            No accounts assigned to this template.
          </td>
        </tr>
      </template>
    </lb-datatableaction>
  </div>
</template>

<script>
import './TaskDetailAccounts.scss';
import { OverflowList } from '@/modules/financialclose/components';

const TABLE_HEADERS = [
  { text: 'GL Code', value: 'glcode', visible: true, sortable: true },
  { text: 'Type', value: 'type', visible: true, sortable: true },
  { text: 'Name', value: 'name', visible: true, sortable: true },
  { text: 'Category', value: 'category', visible: true, sortable: true },
  { text: 'Sub Category', value: 'subcategory', visible: true, sortable: true },
  { text: 'Location', value: 'location', visible: true, sortable: true },
];

function getTableHeaders(locationHeader = 'Location') {
  return TABLE_HEADERS.map((h) =>
    h.value === 'location' ? { ...h, text: locationHeader || 'Location' } : { ...h }
  );
}

function normalizeGlKey(item) {
  if (item == null || item === '') return '';
  if (typeof item === 'object') {
    return String(item._id || item.id || item.glcode || '');
  }
  const asString = String(item);
  if (asString === '[object Object]') return '';
  return asString;
}

function resolveLocationNames(account = {}) {
  const raw = account.location;
  if (!raw) return [];
  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      return (item?.name || item?.location || item?.description || '').trim();
    })
    .filter(Boolean);
}

function buildLocationDisplay(names = []) {
  const list = (names || []).filter(Boolean);
  if (!list.length) {
    return {
      locationPrimary: '',
      locationOverflowCount: 0,
      locationOverflowItems: [],
      locationDisplay: '',
    };
  }
  return {
    locationPrimary: list[0],
    locationOverflowCount: Math.max(0, list.length - 1),
    locationOverflowItems: list.slice(1),
    locationDisplay: list.join(', '),
  };
}

export default {
  name: 'TaskDetailAccounts',

  components: {
    OverflowList,
  },

  props: {
    template: {
      type: Object,
      required: true,
    },
    chartOfAccounts: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    locationHeader() {
      return this.$store.state.LocationHeader || 'Location';
    },

    headers() {
      return getTableHeaders(this.locationHeader);
    },

    visibleHeaders() {
      return this.headers.filter((header) => header.visible !== false);
    },

    templateGlKeys() {
      const keys = new Set();

      // Root glcode (legacy / Accounts-tab pool)
      const root = this.template?.glcode || this.template?.selectgls || [];
      if (Array.isArray(root)) {
        root.map(normalizeGlKey).filter(Boolean).forEach((k) => keys.add(k));
      }

      // Per-location glcodes (current payload shape)
      (this.template?.location || []).forEach((loc) => {
        const gls = loc?.glcode || loc?.glcodes || [];
        if (!Array.isArray(gls)) return;
        gls.map(normalizeGlKey).filter(Boolean).forEach((k) => keys.add(k));
      });

      return keys;
    },

    accountRows() {
      const keys = this.templateGlKeys;
      if (!keys.size) return [];

      return (this.chartOfAccounts || [])
        .filter((account) => {
          const candidates = [account._id, account.id, account.glcode]
            .filter(Boolean)
            .map(String);
          return candidates.some((key) => keys.has(key));
        })
        .map((account) => {
          const locationNames = resolveLocationNames(account);
          const locationDisplay = buildLocationDisplay(locationNames);
          return {
            _id: account._id || account.id,
            glcode: account.glcode || '—',
            type: account.type || account.account_type || '—',
            name: account.name || '—',
            category: account.category || '—',
            subcategory: account.subcategory || '—',
            location: locationDisplay.locationDisplay || '—',
            ...locationDisplay,
          };
        });
    },
  },

  methods: {
    locationItems(item) {
      const extra = Array.isArray(item.locationOverflowItems) ? item.locationOverflowItems : [];
      return [item.locationPrimary, ...extra].filter((value) => {
        const text = value == null ? '' : String(value).trim();
        return text && text !== '—';
      });
    },
  },
};
</script>
